import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Avatar } from "@/components/AppHeader";
import { MobileShell } from "@/components/MobileShell";
import api, { formatApiError } from "@/lib/api";
import { Loader2, Trash2, Shield, ShieldOff, Users, FileText, MessageSquare, Activity } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AdminPage() {
  const { user: me } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
      setStats(s.data);
      setUsers(u.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const deleteUser = async (id) => {
    if (!window.confirm("Delete this user permanently?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      setUsers((u) => u.filter((x) => x.id !== id));
      toast.success("User deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleRole = async (u) => {
    const newRole = u.role === "admin" ? "user" : "admin";
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role: newRole });
      setUsers((arr) => arr.map((x) => x.id === u.id ? { ...x, role: newRole } : x));
      toast.success(`Role updated to ${newRole}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <MobileShell topBar={<div className="h-14 flex items-center px-4"><h1 className="font-heading text-lg font-semibold text-[var(--te-text)]">Admin</h1></div>}>
      <div className="p-4">
        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-[var(--te-primary)]" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-10">
              <Tile label="Users" value={stats.users} icon={Users} />
              <Tile label="Reports" value={stats.reports} icon={FileText} />
              <Tile label="Posts" value={stats.posts} icon={MessageSquare} />
              <Tile label="Comments" value={stats.comments} icon={MessageSquare} />
              <Tile label="Open" value={stats.open_reports} icon={Activity} accent />
              <Tile label="Resolved" value={stats.resolved_reports} icon={Activity} />
            </div>

            <section className="te-card overflow-hidden">
              <header className="p-5 border-b border-[var(--te-input-bg)] flex items-center justify-between">
                <h2 className="font-heading text-xl font-semibold text-[var(--te-text)]">Users</h2>
                <span className="text-sm text-[var(--te-text-muted)]">{users.length} accounts</span>
              </header>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-users-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-[var(--te-text-muted)] border-b border-[var(--te-input-bg)]">
                      <th className="p-4">User</th>
                      <th className="p-4 hidden md:table-cell">Email</th>
                      <th className="p-4 hidden lg:table-cell">City</th>
                      <th className="p-4">Role</th>
                      <th className="p-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-[var(--te-input-bg)] last:border-b-0" data-testid={`admin-user-row-${u.id}`}>
                        <td className="p-4">
                          <Link to={`/profile/${u.id}`} className="flex items-center gap-3 hover:underline">
                            <Avatar user={u} size={32} />
                            <span className="font-medium text-[var(--te-text)]">{u.name}</span>
                          </Link>
                        </td>
                        <td className="p-4 hidden md:table-cell text-[var(--te-text-secondary)]">{u.email}</td>
                        <td className="p-4 hidden lg:table-cell text-[var(--te-text-secondary)]">{u.city || "—"}</td>
                        <td className="p-4">
                          <span className={u.role === "admin" ? "te-badge te-badge-resolved" : "te-badge te-badge-progress"}>{u.role}</span>
                        </td>
                        <td className="p-4 text-right">
                          <div className="inline-flex gap-2">
                            <button
                              onClick={() => toggleRole(u)}
                              disabled={u.id === me?.id}
                              className="te-btn-secondary text-xs py-1.5 px-3 inline-flex items-center gap-1"
                              data-testid={`admin-role-toggle-${u.id}`}
                              title={u.role === "admin" ? "Demote to user" : "Promote to admin"}
                            >
                              {u.role === "admin" ? <ShieldOff size={12} /> : <Shield size={12} />}
                              {u.role === "admin" ? "Demote" : "Promote"}
                            </button>
                            <button
                              onClick={() => deleteUser(u.id)}
                              disabled={u.id === me?.id}
                              className="text-[#DC2626] border border-[var(--te-divider)] rounded-full text-xs py-1.5 px-3 hover:bg-[var(--te-danger-soft)] inline-flex items-center gap-1"
                              data-testid={`admin-delete-user-${u.id}`}
                            >
                              <Trash2 size={12} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </MobileShell>
  );
}

function Tile({ label, value, icon: Icon, accent }) {
  return (
    <div className="te-card p-4">
      <div className="flex items-center justify-between">
        <div className="te-eyebrow">{label}</div>
        <Icon size={14} className={accent ? "text-[#DC2626]" : "text-[var(--te-primary)]"} />
      </div>
      <div className="font-heading text-2xl font-bold text-[var(--te-text)] mt-2">{value}</div>
    </div>
  );
}
